import React from 'react';
import ReactDOM from 'react-dom/client';
import { ThemeProvider, createTheme, responsiveFontSizes } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import axios from 'axios';   // ✅ ADD THIS
import App from './App';


// ✅ GLOBAL AXIOS INTERCEPTOR (VERY IMPORTANT)
axios.interceptors.request.use((config) => {
  // Skip Authorization header for these endpoints to avoid CORS preflight
  const skippedEndpoints = [
    "/api/interviews/log-start",
    "/api/assessments/context/",
    "/api/interviews/ai-interview/more-questions",
    "/api/interviews/ai-interview/submit/",
    "/api/interviews/ai-interview/terminate/"
  ];

  if (config.url && skippedEndpoints.some(endpoint => config.url.includes(endpoint))) {
    return config;
  }

  const token = localStorage.getItem("token");

  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }

  return config;
});


// ── Global Design Tokens ─────────────────────────────────────────────────────
// Primary:       #1E3A8A  (Dark Navy Blue)
// Hover:         #1E40AF
// Active:        #1D4ED8
// Light BG:      #EFF6FF
// Light Border:  #BFDBFE
// Focus Ring:    rgba(30, 58, 138, 0.25)
// ─────────────────────────────────────────────────────────────────────────────

let theme = createTheme({
  breakpoints: {
    values: {
      xs: 0,
      sm: 600,
      md: 900,
      lg: 1366,
      xl: 1600,
      xxl: 1920,
      xxxl: 2560,
    },
  },
  // Spacing in rem so every padding/margin/gap scales with the html font-size.
  // 1 unit = 0.5rem = 8px at the 16px base; shrinks proportionally on smaller viewports.
  spacing: (factor) => `${factor * 0.5}rem`,
  palette: {
    primary: {
      main: '#1E3A8A',        // Dark Navy Blue
      light: '#BFDBFE',       // Light Navy Border
      dark: '#1E40AF',        // Navy Hover
      contrastText: '#FFFFFF',
    },
    secondary: {
      main: '#1E3A8A',
    },
    background: {
      default: '#F8FAFC',
      paper: '#FFFFFF',
    },
    text: {
      primary: '#0A0F2C',
      secondary: '#475569',
      disabled: '#64748B',
    },
    success: { main: '#10B981' },
    warning: { main: '#F59E0B' },
    error:   { main: '#EF4444' },
    divider: '#E5E7EB',
  },
  typography: {
    fontFamily: ['Inter', 'system-ui', 'sans-serif'].join(','),
    h1: { fontWeight: 800, fontSize: 'clamp(2.5rem, 4vw, 3.5rem)', color: '#0A0F2C' },
    h2: { fontWeight: 800, fontSize: 'clamp(2rem, 3.5vw, 3rem)', color: '#0A0F2C' },
    h3: { fontWeight: 800, fontSize: 'clamp(1.75rem, 3vw, 2.5rem)', color: '#0A0F2C' },
    h4: { fontWeight: 700, fontSize: 'clamp(1.5rem, 2.5vw, 2rem)', color: '#0A0F2C' },
    h5: { fontWeight: 700, fontSize: 'clamp(1.25rem, 2vw, 1.5rem)', color: '#0A0F2C' },
    h6: { fontWeight: 600, fontSize: 'clamp(1rem, 1.5vw, 1.125rem)', color: '#0A0F2C' },
    subtitle1: { fontWeight: 500, fontSize: '0.875rem', color: '#475569' },
    body1: { color: '#0A0F2C' },
    body2: { fontSize: '0.8125rem', color: '#475569' },
    button: { textTransform: 'none', fontWeight: 600 },
  },
  shape: {
    borderRadius: 20,
  },
  components: {
    MuiCssBaseline: {
      styleOverrides: `
        *, *::before, *::after { box-sizing: border-box; }

        html {
          /* Scale from 13px at 1366px viewport to 16px at 1600px+.
             All rem-based values shrink proportionally — no manual browser zoom needed. */
          font-size: clamp(13px, 1vw, 16px);
          scroll-behavior: smooth;
          -webkit-text-size-adjust: 100%;
        }

        body {
          overflow-x: hidden;
          -webkit-font-smoothing: antialiased;
          -moz-osx-font-smoothing: grayscale;
        }

        img, video, svg { max-width: 100%; height: auto; }

        /* Accessible focus ring */
        :focus-visible {
          outline: 2px solid #1E3A8A;
          outline-offset: 2px;
          border-radius: 4px;
        }

        /* Tables: always scrollable on overflow */
        .MuiTableContainer-root {
          overflow-x: auto;
          -webkit-overflow-scrolling: touch;
        }

        /* Thin scrollbar */
        ::-webkit-scrollbar { width: 6px; height: 6px; }
        ::-webkit-scrollbar-track { background: transparent; }
        ::-webkit-scrollbar-thumb { background: rgba(30,58,138,0.25); border-radius: 3px; }
        ::-webkit-scrollbar-thumb:hover { background: rgba(30,58,138,0.45); }

        /* Disable card hover lift on touch devices */
        @media (hover: none) {
          .MuiCard-root:hover { transform: none !important; }
        }

        /* Dialogs: stay within viewport on small screens */
        @media (max-width: 600px) {
          .MuiDialog-paper {
            margin: 12px !important;
            max-width: calc(100% - 24px) !important;
            max-height: calc(100% - 24px) !important;
          }
          .MuiDialogTitle-root {
            padding: 12px 16px !important;
            font-size: 1rem !important;
          }
          .MuiDialogContent-root {
            padding: 8px 16px !important;
          }
          .MuiDialogActions-root {
            padding: 8px 16px 16px !important;
            flex-wrap: wrap;
            gap: 8px;
          }
          .MuiDialogActions-root > * {
            flex: 1 1 auto;
            min-width: 100px;
          }
        }

        /* Chips: wrap on narrow containers */
        .MuiChip-root { max-width: 100%; }
        .MuiChip-label { overflow: hidden; text-overflow: ellipsis; }
      `,
    },
    MuiButton: {
      styleOverrides: {
        root: {
          borderRadius: '0.75rem',
          padding: '0.625rem 1.5rem',
          minHeight: '2.75rem',
          boxShadow: 'none',
          '&:hover': {
            boxShadow: '0 4px 12px rgba(30, 58, 138, 0.15)',
          },
          '&.Mui-disabled': {
            backgroundColor: '#CBD5E1 !important',
            color: '#64748B !important',
            borderColor: '#CBD5E1 !important',
            cursor: 'not-allowed !important',
            boxShadow: 'none !important',
            opacity: '0.7 !important',
            pointerEvents: 'auto !important',
            '&:hover': {
              backgroundColor: '#CBD5E1 !important',
              boxShadow: 'none !important',
            },
          },
        },
        containedPrimary: {
          background: 'linear-gradient(135deg, #1E3A8A 0%, #1E40AF 100%)',
          '&:hover': {
            background: 'linear-gradient(135deg, #1E40AF 0%, #1D4ED8 100%)',
          },
          '&.Mui-disabled': {
            background: '#CBD5E1 !important',
          },
        },
        outlinedPrimary: {
          borderColor: '#1E3A8A',
          color: '#1E3A8A',
          '&:hover': {
            backgroundColor: 'rgba(30, 58, 138, 0.04)',
            borderColor: '#1E40AF',
          },
          '&.Mui-disabled': {
            borderColor: '#CBD5E1 !important',
          },
        },
        containedSecondary: {
          backgroundColor: '#1E3A8A',
          '&:hover': {
            backgroundColor: '#1E40AF',
          },
          '&.Mui-disabled': {
            backgroundColor: '#CBD5E1 !important',
          },
        },
        outlinedSecondary: {
          borderColor: '#1E3A8A',
          color: '#1E3A8A',
          '&:hover': {
            backgroundColor: 'rgba(30, 58, 138, 0.04)',
            borderColor: '#1E40AF',
          },
          '&.Mui-disabled': {
            borderColor: '#CBD5E1 !important',
          },
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          borderRadius: '1.25rem',
          backgroundColor: '#FFFFFF',
          border: '1px solid #E5E7EB',
          boxShadow: '0 4px 20px rgba(10,15,44,0.05)',
          transition: '200ms ease-out',
          '&:hover': {
            transform: 'translateY(-4px)',
          },
        },
      },
    },
    MuiLink: {
      defaultProps: {
        underline: 'hover',
      },
      styleOverrides: {
        root: {
          color: '#1E3A8A',
          fontWeight: 600,
          '&:hover': { color: '#1E40AF' },
        },
      },
    },
    MuiInputLabel: {
      styleOverrides: {
        root: {
          color: '#475569',
          '&.Mui-focused': { color: '#1E3A8A' },
        },
      },
    },
    MuiOutlinedInput: {
      styleOverrides: {
        root: {
          '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
            borderColor: '#1E3A8A',
          },
        },
        input: {
          '::placeholder': { color: '#64748B', opacity: 1 },
        },
      },
    },
    MuiCheckbox: {
      styleOverrides: {
        root: {
          color: '#9CA3AF',
          '&.Mui-checked': { color: '#1E3A8A' },
        },
      },
    },
    MuiRadio: {
      styleOverrides: {
        root: {
          color: '#9CA3AF',
          '&.Mui-checked': { color: '#1E3A8A' },
        },
      },
    },
    MuiSwitch: {
      styleOverrides: {
        switchBase: {
          '&.Mui-checked': {
            color: '#1E3A8A',
            '& + .MuiSwitch-track': { backgroundColor: '#1E3A8A' },
          },
        },
      },
    },
    MuiLinearProgress: {
      styleOverrides: {
        bar: { backgroundColor: '#1E3A8A' },
      },
    },
    MuiCircularProgress: {
      styleOverrides: {
        circle: { stroke: '#1E3A8A' },
      },
    },
    MuiPagination: {
      styleOverrides: {
        root: {
          '& .Mui-selected': {
            backgroundColor: '#1E3A8A !important',
            color: '#FFFFFF !important',
          },
        },
      },
    },
    MuiTab: {
      styleOverrides: {
        root: {
          '&.Mui-selected': { color: '#1E3A8A' },
        },
      },
    },
    MuiTabs: {
      styleOverrides: {
        indicator: { backgroundColor: '#1E3A8A' },
      },
    },
    MuiChip: {
      styleOverrides: {
        colorPrimary: {
          backgroundColor: '#EFF6FF',
          color: '#1E3A8A',
          border: '1px solid #BFDBFE',
        },
      },
    },
    MuiIconButton: {
      styleOverrides: {
        root: {
          minWidth: '2.75rem',
          minHeight: '2.75rem',
        },
      },
    },
    MuiTableContainer: {
      styleOverrides: {
        root: {
          overflowX: 'auto',
          WebkitOverflowScrolling: 'touch',
          maxWidth: '100%',
        },
      },
    },
    MuiTable: {
      defaultProps: {
        size: 'medium',
      },
    },
    MuiContainer: {
      defaultProps: {
        maxWidth: false,
      },
    },
    MuiTooltip: {
      defaultProps: {
        enterTouchDelay: 700,
        leaveTouchDelay: 1500,
      },
    },
    MuiDialog: {
      defaultProps: {
        fullWidth: true,
      },
    },
    MuiSelect: {
      styleOverrides: {
        select: {
          minHeight: '1.4375em',
        },
      },
    },
    MuiTextField: {
      defaultProps: {
        variant: 'outlined',
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: {
          backgroundImage: 'none',
        },
      },
    },
    MuiStack: {
      defaultProps: {
        useFlexGap: true,
      },
    },
  },
});
theme = responsiveFontSizes(theme);
const root = ReactDOM.createRoot(document.getElementById('root'));
root.render(
    <React.StrictMode>
      <ThemeProvider theme={theme}>
        <CssBaseline />
        <App />
      </ThemeProvider>
    </React.StrictMode>
);
